import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { DevicesClass } from '@app/core/classes';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { GenresService } from '@app/modules/genres-modules/genres.service';
import { GenreModel, GenresModel } from '@app/modules/fundamentals-modules/fundamentals.models';
import { FundamentalsService } from '@app/modules/fundamentals-modules/fundamentals.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DialogIncludesTypes, DialogComponentModel } from '@app/core/models/diallog';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { debounceTime, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-search-genre',
  templateUrl: 'search-genre.component.html',
  styleUrls: [`./search-genre.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class SearchGenreComponent extends DevicesClass implements OnInit {
  /**
   * Base component variables.
   */
  public SGenresForm: FormGroup;
  public SResult: GenresModel = this.fundamentalsService.genresRecommendations;
  public SGenresValue: GenresModel = [];

  public SInputConfig: DialogComponentModel = null;
  public STooltipDisabled: boolean = false;

  /** Get the limit of selection */
  get maxSelection(): number {
    return (this.SInputConfig?.includeData as any)?.maxSelection ?? 5;
  }

  constructor(injector: Injector,
    public cdr: ChangeDetectorRef,
    private router: Router,
    private genresService: GenresService,
    private fundamentalsService: FundamentalsService,
    private formBuilder: FormBuilder,
    private dialogConfig: DynamicDialogConfig,
    private dialogRef: DynamicDialogRef
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.SGenresForm = this.formBuilder.group({
      genre: ['']
    });

    /**
     * Form changes control.
     */
    this.onSearchGenreCtrl();

    /**
     * Dialog input config.
     */
    this.onSearchGenreInputConfig();
  }

  /**
   * Apply Dialog Input config.
   * @type DialogComponentModel
   */
  onSearchGenreInputConfig(): void {
    this.SInputConfig = <DialogComponentModel>this.dialogConfig.data || null;

    if (this.SInputConfig && this.SInputConfig.hasOwnProperty('includeData')) {
      const DataInitial = <GenreModel>this.SInputConfig.includeData.dataInitial || null;

      if (DataInitial) {
        this.onSearchGenrePatchForm(DataInitial);
      }
    }
  }

  onSearchGenreCtrl(): void {
    this.SGenresForm.valueChanges.pipe(
      untilDestroyed(this),
      map(value => {
        /**
         * Display of tooltip
         */
        value.genre === '' ? this.STooltipDisabled = false : this.STooltipDisabled = true;

        return value;
      }),
      debounceTime(500)
    ).subscribe(value => {
      if (value.genre === '') {
        return this.onApplySearchGenreResult(
          this.fundamentalsService.genresRecommendations
        )
      }
      this.onSearchGenreAPI(value.genre);
    });
  }

  onSearchGenreAPI(genreName: string): void {
    this.genresService.genresSearch(genreName).subscribe(
      response => this.onApplySearchGenreResult(response),
      error => this.onApplySearchGenreResult([])
    )
  }

  onSearchGenrePatchForm(dataInitial: any): void {
    if (dataInitial.hasOwnProperty('genres')) {
      dataInitial.genres.map(genre => {
        this.SResult.map(inst => {
          if (inst.id === genre.id) {
            inst.selected = true;
          }
        });
        this.onSetSelectedGenre(genre);
      });
    }
  }

  onViewAllGenres($event: Event): void {
    $event.preventDefault();

    this.onApplySearchGenreResult(
      this.fundamentalsService.genresDependencies
    );
  }

  onInputKeyup($event: KeyboardEvent): void {
    if ($event.key === 'Backspace' || $event.key === 'Delete') {
      this.cdr.markForCheck();
    }
  }

  onApplySearchGenreResult(genres: GenresModel): void {
    this.SResult = genres.length ? genres : null;

    if (this.SResult && this.SGenresValue) {
      this.SGenresValue.map(value => {
        this.SResult.map(inst => {
          if (inst.id === value.id) {
            inst.selected = true;
          }
        });
      });
    }

    this.cdr.markForCheck();
  }

  onSetSelectedGenre(instrument: GenreModel, $event?: Event) {
    if (this.SGenresValue.some(inst => inst.id === instrument.id)) {
      return this.onRemoveSelectedGenre(instrument);
    }

    const maxSelection = this.maxSelection;
    if (this.SGenresValue.length >= maxSelection) {
      return $event.target['checked'] = false;
    }
    instrument.selected = true;
    this.SGenresValue.push(instrument);

    this.cdr.markForCheck();
  }

  onRemoveSelectedGenre(instrument: GenreModel) {
    if (this.SResult) {
      this.SResult.map(inst => {
        if (inst.id === instrument.id) {
          inst.selected = false;
        }
      });
    }


    this.SGenresValue = this.SGenresValue.filter(inst => {
      return inst.id !== instrument.id;
    });
  }


  onSubmitForm(): void {
    /**
     * Callback if global using.
     * @type PopupTemplate
     */
    if (this.SInputConfig.includeType === DialogIncludesTypes.PopupTemplate) {
      console.log({
        event: 'Push global Activity',
        data: {
          genres: this.SGenresValue
        }
      })
    }

    /**
     * Callback if using in component.
     * @type ComponentTemplate
     */
    if (this.SInputConfig.includeType === DialogIncludesTypes.ComponentTemplate) {
      this.SInputConfig.includeData.dataSubject.next({
        genres: this.SGenresValue
      });
    }

    /**
     * Close popup.
     */
    this.dialogRef.close();
  }
}
